/**
 * Offline queue for meter readings (F-910). Submissions that fail because the device is offline are kept
 * (incl. photos as Blobs) and sent again later with the SAME `client_uuid` – the API records idempotently.
 *
 * Storage is abstracted (`getAll`/`put`/`delete`) so that tests can use the in-memory store; in the browser
 * IndexedDB is used.
 *
 * @typedef {{
 *   client_uuid: string,
 *   reading_id: number|string,
 *   value: number,
 *   is_new_meter: boolean,
 *   photo?: Blob|null,
 *   photo_2?: Blob|null,
 *   meter_number?: string|null,
 *   created_at: string,
 *   error?: string|null,
 * }} QueuedReading
 *
 * @typedef {{
 *   getAll: () => Promise<QueuedReading[]>,
 *   put: (entry: QueuedReading) => Promise<void>,
 *   delete: (clientUuid: string) => Promise<void>,
 * }} OfflineStore
 */

/** In-memory store (tests, browsers without IndexedDB). @param {QueuedReading[]} [initial] @returns {OfflineStore} */
export function createMemoryStore(initial = []) {
  const items = new Map(initial.map((entry) => [entry.client_uuid, entry]));
  return {
    getAll: async () => [...items.values()],
    put: async (entry) => {
      items.set(entry.client_uuid, entry);
    },
    delete: async (clientUuid) => {
      items.delete(clientUuid);
    },
  };
}

const DB_NAME = 'blimo-portal';
const STORE_NAME = 'meter-readings';

/** @param {IDBRequest} request */
const done = (request) =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

/** IndexedDB store (DB `blimo-portal`, object store `meter-readings`, keyPath `client_uuid`). @param {IDBFactory} [factory] @returns {OfflineStore} */
export function createIndexedDbStore(factory = globalThis.indexedDB) {
  /** @type {Promise<IDBDatabase>|null} */
  let dbPromise = null;
  const db = () => {
    if (!dbPromise) {
      const request = factory.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: 'client_uuid' });
      };
      dbPromise = done(request);
    }
    return dbPromise;
  };
  /** @param {IDBTransactionMode} mode */
  const store = async (mode) => (await db()).transaction(STORE_NAME, mode).objectStore(STORE_NAME);
  return {
    getAll: async () => done((await store('readonly')).getAll()),
    put: async (entry) => {
      await done((await store('readwrite')).put(entry));
    },
    delete: async (clientUuid) => {
      await done((await store('readwrite')).delete(clientUuid));
    },
  };
}

/** IndexedDB when available, otherwise memory (lost on reload). */
export function defaultOfflineStore() {
  return typeof globalThis.indexedDB !== 'undefined' && globalThis.indexedDB ? createIndexedDbStore() : createMemoryStore();
}

/** Offline / unreachable: `ProblemError` with status 0 (api-client) or a raw fetch TypeError. @param {any} error */
export function isNetworkError(error) {
  return error?.status === 0 || error?.isNetworkError === true || error instanceof TypeError;
}

/** Worth retrying later without user action (offline, session, throttling, server errors). @param {any} error */
function isTransient(error) {
  if (isNetworkError(error)) return true;
  const status = error?.status;
  return status === 401 || status === 419 || status === 429 || (typeof status === 'number' && status >= 500);
}

/** RFC 4122 v4 – `crypto.randomUUID` where available. */
export function newClientUuid() {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * @param {{ store: OfflineStore, send: (entry: QueuedReading) => Promise<unknown> }} options
 */
export function createSubmissionQueue({ store, send }) {
  /** @type {Set<(entries: QueuedReading[]) => void>} */
  const listeners = new Set();
  /** @type {Promise<{ sent: number }>|null} */
  let running = null;

  const list = async () => (await store.getAll()).sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
  const notify = async () => {
    const entries = await list();
    listeners.forEach((listener) => listener(entries));
  };

  /** Sends all entries without error, oldest first; stops at the first transient failure. */
  async function run() {
    let sent = 0;
    for (const entry of await list()) {
      if (entry.error) continue;
      try {
        await send(entry);
        await store.delete(entry.client_uuid);
        sent += 1;
      } catch (error) {
        if (isTransient(error)) break;
        await store.put({ ...entry, error: /** @type {any} */ (error)?.detail || /** @type {any} */ (error)?.message || 'Übertragung abgelehnt.' });
      }
    }
    await notify();
    return { sent };
  }

  return {
    list,
    /** @param {QueuedReading} entry */
    enqueue: async (entry) => {
      await store.put({ ...entry, error: null });
      await notify();
    },
    /** Concurrent calls share one run. @returns {Promise<{ sent: number }>} */
    flush: () => {
      if (!running) running = run().finally(() => (running = null));
      return running;
    },
    /** @param {string} clientUuid */
    discard: async (clientUuid) => {
      await store.delete(clientUuid);
      await notify();
    },
    /** @param {(entries: QueuedReading[]) => void} listener */
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
